import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { FaChevronDown, FaEllipsisV } from "react-icons/fa";

const HOVER_CLOSE_MS = 160;
const MENU_WIDTH = 220;

function toneClass(tone) {
  if (tone === "danger") return "cms-commissions-row-actions__item--danger";
  if (tone === "success") return "cms-commissions-row-actions__item--success";
  if (tone === "muted") return "cms-commissions-row-actions__item--muted";
  return "";
}

function ActionMenuItem({ item, itemDisabled, onSelect }) {
  const tone = toneClass(item.tone);
  const disabledClass =
    itemDisabled || item.disabled ? "cms-commissions-row-actions__item--disabled" : "";

  const inner = (
    <span
      className={`cms-commissions-row-actions__item-inner${
        item.countBadge != null ? " cms-commissions-row-actions__item-inner--spread" : ""
      }`}
    >
      <span className="cms-commissions-row-actions__icon" aria-hidden="true">
        {item.icon}
      </span>
      <span className="cms-commissions-row-actions__label">{item.label}</span>
      {item.countBadge != null ? (
        <span className="cms-commissions-row-actions__count-badge" aria-hidden="true">
          {item.countBadge}
        </span>
      ) : null}
    </span>
  );

  if (item.type === "link") {
    if (itemDisabled || !item.to) {
      return (
        <span
          className={`cms-commissions-row-actions__item ${tone} ${disabledClass}`}
          title={item.title}
        >
          {inner}
        </span>
      );
    }

    return (
      <Link
        to={item.to}
        className={`cms-commissions-row-actions__item ${tone}`}
        title={item.title || item.label}
        onClick={() => onSelect?.()}
      >
        {inner}
      </Link>
    );
  }

  return (
    <button
      type="button"
      className={`cms-commissions-row-actions__item ${tone}`}
      title={item.title || item.label}
      disabled={itemDisabled || item.disabled}
      onClick={() => {
        if (itemDisabled || item.disabled) return;
        item.onClick?.();
        onSelect?.();
      }}
    >
      {inner}
    </button>
  );
}

export default function CommissionsRowActionsToolbar({
  items,
  disabled,
  ariaLabel,
  triggerMode = "icon",
  triggerLabel = "Actions",
  toolbarClassName = "",
  menuWidth = MENU_WIDTH,
  openOnHover = false,
  menuTitle = null,
}) {
  const visibleItems = (items || []).filter(
    (item) => item.show && item.tone !== "muted",
  );

  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});
  const [placement, setPlacement] = useState("bottom");

  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const closeTimerRef = useRef(null);
  const menuId = useId();

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const close = useCallback(() => {
    clearCloseTimer();
    setOpen(false);
  }, [clearCloseTimer]);

  const positionMenu = useCallback(() => {
    const trigger = triggerRef.current;
    const menu = menuRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const menuHeight = menu?.offsetHeight || 240;
    const gap = 6;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openAbove = spaceBelow < menuHeight + gap && rect.top > spaceBelow;

    setPlacement(openAbove ? "top" : "bottom");
    setMenuStyle({
      position: "fixed",
      left: Math.max(8, rect.right - menuWidth),
      top: openAbove ? undefined : rect.bottom + gap,
      bottom: openAbove ? window.innerHeight - rect.top + gap : undefined,
      width: menuWidth,
      zIndex: 10050,
      visibility: "visible",
    });
  }, [menuWidth]);

  const openMenu = useCallback(() => {
    if (disabled || visibleItems.length === 0) return;
    clearCloseTimer();
    setOpen(true);
  }, [clearCloseTimer, disabled, visibleItems.length]);

  const scheduleClose = useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => setOpen(false), HOVER_CLOSE_MS);
  }, [clearCloseTimer]);

  useLayoutEffect(() => {
    if (!open) return;
    positionMenu();
    const frame = requestAnimationFrame(() => positionMenu());
    const onScrollOrResize = () => positionMenu();
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("scroll", onScrollOrResize, true);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", onScrollOrResize);
      window.removeEventListener("scroll", onScrollOrResize, true);
    };
  }, [open, positionMenu, visibleItems.length]);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event) => {
      const root = rootRef.current;
      const menu = menuRef.current;
      if (root?.contains(event.target) || menu?.contains(event.target)) {
        return;
      }
      close();
    };

    const onKeyDown = (event) => {
      if (event.key === "Escape") close();
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, close]);

  useEffect(() => () => clearCloseTimer(), [clearCloseTimer]);

  if (visibleItems.length === 0) {
    return <span className="text-gray-300">—</span>;
  }

  const menu = open
      ? createPortal(
          <div
            ref={menuRef}
            id={menuId}
            role="menu"
            className={`cms-commissions-row-actions__menu cms-commissions-row-actions__menu--compact cms-commissions-row-actions__menu--${placement}`}
            style={{
              ...menuStyle,
              visibility: menuStyle.visibility || "hidden",
            }}
            onMouseEnter={clearCloseTimer}
            onMouseLeave={scheduleClose}
          >
            {menuTitle ? (
              <div className="cms-commissions-row-actions__menu-title">{menuTitle}</div>
            ) : null}
            <div className="cms-commissions-row-actions__menu-body">
              {visibleItems.map((item) => (
                <ActionMenuItem
                  key={item.key}
                  item={item}
                  itemDisabled={disabled}
                  onSelect={close}
                />
              ))}
            </div>
          </div>,
          document.body,
        )
      : null;

  const isLabeledTrigger = triggerMode === "labeled";
  const hoverOpens = openOnHover || !isLabeledTrigger;

  return (
    <div
      ref={rootRef}
      className={`cms-commissions-row-actions inline-flex justify-end ${toolbarClassName}`.trim()}
      onMouseEnter={hoverOpens ? openMenu : undefined}
      onMouseLeave={hoverOpens ? scheduleClose : undefined}
    >
      <button
        ref={triggerRef}
        type="button"
        className={`cms-commissions-row-actions__trigger ${
          isLabeledTrigger ? "cms-commissions-row-actions__trigger--labeled" : ""
        } ${open ? "cms-commissions-row-actions__trigger--open" : ""}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={ariaLabel || "Row actions"}
        disabled={disabled}
        style={{ boxShadow: "none" }}
        onClick={(event) => {
          event.stopPropagation();
          if (hoverOpens) {
            if (!open) openMenu();
            return;
          }
          if (open) close();
          else openMenu();
        }}
      >
        {isLabeledTrigger ? (
          <>
            <span className="cms-commissions-row-actions__trigger-text">
              {triggerLabel}
            </span>
            <FaChevronDown
              size={10}
              className="cms-commissions-row-actions__chevron"
              aria-hidden="true"
            />
          </>
        ) : (
          <FaEllipsisV size={14} aria-hidden="true" />
        )}
      </button>
      {menu}
    </div>
  );
}
